import { _parseTimeExpression } from '@dynatrace-sdk/units';

/**
 * Matches relative timeframe expressions like `now-2d`, `now()+1h`, `-3w@w3`, `+1d@d`, `now`.
 * Groups: (1) now-prefix, (2) signed number, (3) unit + optional rounding
 * https://regex101.com/r/Ocriyz/1
 */
function checkFormat(content: string): RegExpExecArray | null {
  return /^(now\(\)|now|Now)?([+-]?\d+)?([a-zA-Z][a-zA-Z0-9@]*)?$/.exec(
    content,
  );
}

/** Returns true if the suffix (unit + optional rounding) is recognised by the SDK parser. */
function isValidSuffix(
  numberPart: string | undefined,
  suffix: string,
): boolean {
  const hasSign = numberPart?.startsWith('+') || numberPart?.startsWith('-');
  const candidate = `now()${hasSign ? '' : '+'}${numberPart ?? '0'}${suffix}`;
  return Boolean(_parseTimeExpression(candidate));
}

/** Formats a signed integer as a string, adding an explicit '+' for non-negative values when a prefix is present. */
function formatNumber(value: number, hasPrefix: boolean): string {
  if (value > 0 || (value >= 0 && hasPrefix)) {
    return `+${value}`;
  }
  return String(value);
}

/**
 * Increments or decrements the number in a relative timeframe expression.
 *
 * @param currentText - The current expression (e.g. `now+1d`, `-2h`, `now`)
 * @param direction - +1 to increment, -1 to decrement
 * @returns The updated expression, or null if not spinnable
 *
 * @example
 * spinExpressionValue('now+1d', 1)   // 'now+2d'
 * spinExpressionValue('now+1d', -1)  // 'now'
 * spinExpressionValue('-2h', -1)     // '-3h'
 * spinExpressionValue('now', 1)      // 'now+1'
 * spinExpressionValue('+', 1)        // '+1'
 */
export function spinExpressionValue(
  currentText: string,
  direction: 1 | -1,
): string | null {
  const trimmed = currentText.trim();
  if (!trimmed) {
    return null;
  }

  // Bare '+' or '-' with no number yet — treat as 0
  if (trimmed === '+' || trimmed === '-') {
    return formatNumber(direction, false);
  }

  const match = checkFormat(trimmed);

  if (!match) {
    return null;
  }

  const [, rawPrefix = '', numberPart, suffix = ''] = match;

  if (!rawPrefix && !numberPart) {
    return null;
  }

  if (suffix && !isValidSuffix(numberPart, suffix)) {
    return null;
  }

  const prefix = rawPrefix === 'Now' ? 'now' : rawPrefix;
  const currentNumber = numberPart ? Number.parseInt(numberPart, 10) : 0;

  if (Number.isNaN(currentNumber)) {
    return null;
  }

  const newNumber = currentNumber + direction;

  // A now-prefixed expression without a unit collapses to 'now' at 0 (e.g. 'now+1' → 'now')
  // With a unit the 0 is preserved so the unit is not lost (e.g. 'now+1h' → 'now+0h')
  if (prefix && newNumber === 0 && !suffix) {
    return prefix;
  }

  return `${prefix}${formatNumber(newNumber, prefix !== '')}${suffix}`;
}
